"use client";

import { useCallback, useRef, useState } from "react";
import { classNames } from "../ui/class-names";

export const minSplitRatio = 0.2;
export const maxSplitRatio = 0.8;
export const keyboardSplitStep = 0.05;
/** Symmetric with the editor so scale-to-fit can shrink below native page width. */
export const minPreviewWidthPx = 320;
export const minEditorWidthPx = 320;

/** Clamp the divider x so both panes keep their pixel floors; midpoint if they cannot. */
export function clampSplitPointerX(
  xInContainer: number,
  containerWidth: number,
  minEditor = minEditorWidthPx,
  minPreview = minPreviewWidthPx,
) {
  let minLeftX = minEditor;
  let maxLeftX = containerWidth - minPreview;

  if (maxLeftX < minLeftX) {
    const mid = containerWidth / 2;
    minLeftX = mid;
    maxLeftX = mid;
  }

  return Math.min(Math.max(xInContainer, minLeftX), maxLeftX);
}

export function ResizableSplit({
  children,
  ratio,
  onRatioChange,
}: Readonly<{
  children: [React.ReactNode, React.ReactNode];
  ratio: number;
  onRatioChange: (ratio: number) => void;
}>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const leftRatio = Math.min(Math.max(ratio, minSplitRatio), maxSplitRatio);

  const commitRatio = useCallback((nextRatio: number) => {
    const clamped = Math.min(Math.max(nextRatio, minSplitRatio), maxSplitRatio);

    if (clamped !== leftRatio) {
      onRatioChange(clamped);
    }
  }, [leftRatio, onRatioChange]);

  const updateRatio = useCallback((clientX: number) => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const clampedX = clampSplitPointerX(clientX - rect.left, rect.width);
    const nextRatio = clampedX / rect.width;

    commitRatio(nextRatio);
  }, [commitRatio]);

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!isDragging) {
      return;
    }

    updateRatio(event.clientX);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (!isDragging) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      commitRatio(leftRatio - keyboardSplitStep);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      commitRatio(leftRatio + keyboardSplitStep);
    } else if (event.key === "Home") {
      event.preventDefault();
      const nextRatio = (() => {
        const container = containerRef.current;
        if (!container) return Math.max(minSplitRatio, minEditorWidthPx / window.innerWidth);
        return Math.max(minSplitRatio, minEditorWidthPx / container.clientWidth);
      })();
      commitRatio(nextRatio);
    } else if (event.key === "End") {
      event.preventDefault();
      const nextRatio = (() => {
        const container = containerRef.current;
        if (!container) return Math.min(maxSplitRatio, 1 - minPreviewWidthPx / window.innerWidth);
        return Math.min(maxSplitRatio, 1 - minPreviewWidthPx / container.clientWidth);
      })();
      commitRatio(nextRatio);
    }
  }

  return (
    <div
      ref={containerRef}
      className={classNames(
        "grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[var(--split-left,50%)_auto_minmax(0,1fr)]",
        isDragging && "cursor-col-resize",
      )}
      style={
        {
          "--split-left": `${leftRatio * 100}%`,
          transition: isDragging ? undefined : "grid-template-columns 180ms ease-out",
          userSelect: isDragging ? "none" : undefined,
        } as React.CSSProperties
      }
    >
      {children[0]}
      <div className="group relative hidden md:block">
        <div
          className={classNames(
            "h-full w-px transition-colors",
            isDragging ? "bg-brand" : "bg-zinc-200 group-hover:bg-brand-muted",
          )}
        />
        <div
          role="separator"
          aria-label="Resize editor and preview panes"
          aria-orientation="vertical"
          aria-valuenow={Math.round(leftRatio * 100)}
          aria-valuemin={Math.round(minSplitRatio * 100)}
          aria-valuemax={Math.round(maxSplitRatio * 100)}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onKeyDown={handleKeyDown}
          className="absolute inset-y-0 left-1/2 z-10 w-5 -translate-x-1/2 touch-none cursor-col-resize bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        />
      </div>
      {children[1]}
    </div>
  );
}
